import React, { useEffect, useState } from 'react';
import UserBadge from '../components/UserBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest, UPLOADS_BASE_URL } from '../config/api';
import { authentikConfig } from '../config/authentik.config';

interface AircraftEntry {
  _id: string;
  identification: string;
  name: string;
  type: string;
  sensorid?: {
    _id: string;
    type: string;
    bluetoothId?: string;
  };
}

const AIRCRAFT_TYPE_LABELS: Record<string, string> = {
  balloon: 'Ballon',       ballon: 'Ballon',
  glider: 'Segelflugzeug', segelflugzeug: 'Segelflugzeug',
  plane: 'Motorflugzeug',  motorflugzeug: 'Motorflugzeug',
  helicopter: 'Hubschrauber', hubschrauber: 'Hubschrauber',
};

const ROLE_LABELS: Record<string, string> = {
  flugschule: 'Flugschule',
  pilot:      'Pilot',
};

const Settings: React.FC = () => {
  const { userDetails, refreshUserDetails } = useAuth();

  // Profile form state
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Privacy toggle
  const [flightsPublic, setFlightsPublic] = useState(false);
  const [privacySaving, setPrivacySaving] = useState(false);

  // Aircraft list
  const [aircraft, setAircraft] = useState<AircraftEntry[]>([]);
  const [aircraftLoading, setAircraftLoading] = useState(true);

  // Seed form from userDetails on load
  useEffect(() => {
    if (userDetails) {
      setName(userDetails.name || '');
      setOrganization(userDetails.organization || '');
      setFlightsPublic(userDetails.defaultFlightVisibility === 'public');
    }
  }, [userDetails]);

  // Fetch user's aircraft (with sensors populated)
  useEffect(() => {
    if (!userDetails?.userid) return;
    const fetchAircraft = async () => {
      try {
        const response = await apiRequest(`/aircraft/user/${userDetails.userid}`);
        if (response.ok) setAircraft(await response.json());
      } finally {
        setAircraftLoading(false);
      }
    };
    fetchAircraft();
  }, [userDetails?.userid]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileSaved(false);
    setProfileError(null);
    try {
      const body = new FormData();
      body.append('name', name);
      body.append('organization', organization);
      if (logoFile) body.append('logo', logoFile);

      const response = await apiRequest('/users/me', { method: 'PUT', body });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Speichern');
      }
      await refreshUserDetails();
      setLogoFile(null);
      setLogoPreview(null);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePrivacyToggle = async (checked: boolean) => {
    setFlightsPublic(checked);
    setPrivacySaving(true);
    try {
      const body = new FormData();
      body.append('defaultFlightVisibility', checked ? 'public' : 'private');
      await apiRequest('/users/me', { method: 'PUT', body });
      await refreshUserDetails();
    } catch {
      // Revert on error
      setFlightsPublic(!checked);
    } finally {
      setPrivacySaving(false);
    }
  };

  const currentLogoUrl = logoPreview
    ? logoPreview
    : userDetails?.logo
    ? `${UPLOADS_BASE_URL}${userDetails.logo}`
    : null;

  return (
    <div className="container" style={{ padding: '40px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 style={{ color: '#687088', fontWeight: 500, margin: 0 }}>Einstellungen</h3>
        <UserBadge />
      </div>

      {/* Profil */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '20px' }}>Profil</h5>
          <form onSubmit={handleProfileSave}>
            {profileError && <div className="alert alert-danger">{profileError}</div>}
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                  Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                  Rolle
                </label>
                <input
                  type="text"
                  className="form-control"
                  readOnly
                  value={ROLE_LABELS[userDetails?.userRole || ''] || userDetails?.userRole || ''}
                  style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                />
              </div>
              <div className="col-12">
                <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                  Organisation
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                  Profilbild
                </label>
                <div className="d-flex align-items-center gap-3">
                  {currentLogoUrl && (
                    <img
                      src={currentLogoUrl}
                      alt="Profilbild"
                      style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e0e4ef' }}
                    />
                  )}
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 mt-4 flex-wrap">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ fontWeight: 700, fontSize: '13px' }}
                disabled={profileSaving}
              >
                {profileSaving ? 'Speichern...' : 'Speichern'}
              </button>
              {profileSaved && (
                <span style={{ color: '#198754', fontSize: '13px' }}>Gespeichert</span>
              )}
              <a
                href={`${authentikConfig.authentikUrl}/if/user/`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-secondary"
                style={{ fontWeight: 600, fontSize: '13px' }}
              >
                Login-Einstellungen ändern →
              </a>
            </div>
          </form>
        </div>
      </div>

      {/* Datenschutz */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Datenschutz</h5>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="flightsPublicSwitch"
              checked={flightsPublic}
              disabled={privacySaving}
              onChange={(e) => handlePrivacyToggle(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label
              className="form-check-label"
              htmlFor="flightsPublicSwitch"
              style={{ color: '#687088', fontSize: '14px' }}
            >
              Flüge standardmäßig öffentlich anzeigen
            </label>
          </div>
        </div>
      </div>

      {/* Meine Flugzeuge & Sensoren */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Meine Flugzeuge & Sensoren</h5>
          {aircraftLoading && <LoadingSpinner message="Lade Flugzeuge..." />}
          {!aircraftLoading && aircraft.length === 0 && (
            <p style={{ color: '#a0afc0', fontSize: '14px', margin: 0 }}>Keine Flugzeuge hinterlegt.</p>
          )}
          {aircraft.map((a, i) => (
            <div
              key={a._id}
              style={{
                paddingTop: i > 0 ? '14px' : 0,
                marginTop: i > 0 ? '14px' : 0,
                borderTop: i > 0 ? '1px solid #e0e4ef' : 'none',
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span style={{ color: '#687088', fontWeight: 700, fontSize: '14px' }}>
                    {a.identification}
                  </span>
                  {a.name !== a.identification && (
                    <span style={{ color: '#687088', fontSize: '14px' }}> · {a.name}</span>
                  )}
                  <span style={{ color: '#a0afc0', fontSize: '13px' }}>
                    {' '}· {AIRCRAFT_TYPE_LABELS[a.type] || a.type}
                  </span>
                </div>
                {a.sensorid && (
                  <span
                    style={{
                      background: '#e8f4fd',
                      color: '#0d6efd',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '3px 10px',
                      borderRadius: '20px',
                    }}
                  >
                    Sensor
                  </span>
                )}
              </div>
              {a.sensorid && (
                <p style={{ color: '#a0afc0', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
                  {a.sensorid.type}
                  {a.sensorid.bluetoothId ? ` · BT: ${a.sensorid.bluetoothId}` : ''}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;
